import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Folder, File as FileIcon, ChevronRight, ArrowLeft, RefreshCw,
  Plus, Trash2, Download, Archive, ArchiveRestore, Edit3,
  Loader2, X, FolderPlus, FilePlus, Upload, Save,
  Check, AlertTriangle, FileCode, FileText, Image, Music,
  Settings, FileJson, Database as DatabaseIcon, Copy
} from 'lucide-react';
import { hostingAPI } from '../../lib/api';
import { HostedServer } from '../../store/hostingStore';
import Editor, { loader } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';
import editorWorker from 'monaco-editor/esm/vs/editor/editor.worker?worker';
import jsonWorker from 'monaco-editor/esm/vs/language/json/json.worker?worker';
import cssWorker from 'monaco-editor/esm/vs/language/css/css.worker?worker';
import tsWorker from 'monaco-editor/esm/vs/language/typescript/ts.worker?worker';
import htmlWorker from 'monaco-editor/esm/vs/language/html/html.worker?worker';

// Configure Monaco workers for Vite + Electron (CSP blocks CDN)
self.MonacoEnvironment = {
  getWorker(_: unknown, label: string) {
    if (label === 'json') return new jsonWorker();
    if (label === 'css' || label === 'scss' || label === 'less') return new cssWorker();
    if (label === 'html' || label === 'handlebars' || label === 'razor') return new htmlWorker();
    if (label === 'typescript' || label === 'javascript') return new tsWorker();
    return new editorWorker();
  },
};
loader.config({ monaco });

// ── Types ────────────────────────────────────────────────────────────────────

interface ServerFile {
  name: string;
  mode: string;
  size: number;
  isFile: boolean;
  isSymlink: boolean;
  mimetype: string;
  createdAt: string;
  modifiedAt: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const MAX_EDITABLE_SIZE = 1024 * 1024; // 1 MB

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function getFileLanguage(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  const map: Record<string, string> = {
    json: 'json', yml: 'yaml', yaml: 'yaml', xml: 'xml',
    properties: 'ini', cfg: 'ini', conf: 'ini', ini: 'ini',
    toml: 'ini', txt: 'plaintext', log: 'plaintext', md: 'markdown',
    js: 'javascript', ts: 'typescript', sh: 'shell', bash: 'shell',
    py: 'python', java: 'java', html: 'html', css: 'css',
    sql: 'sql', sk: 'plaintext', mcmeta: 'json',
  };
  return map[ext] || 'plaintext';
}

function getFileIcon(file: ServerFile) {
  if (!file.isFile) return <Folder className="w-4 h-4 text-cyan-400" />;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (['json', 'mcmeta'].includes(ext)) return <FileJson className="w-4 h-4 text-yellow-400" />;
  if (['yml', 'yaml', 'toml', 'properties', 'cfg', 'conf', 'ini'].includes(ext)) return <Settings className="w-4 h-4 text-blue-400" />;
  if (['js', 'ts', 'py', 'java', 'sh', 'sk'].includes(ext)) return <FileCode className="w-4 h-4 text-emerald-400" />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico'].includes(ext)) return <Image className="w-4 h-4 text-purple-400" />;
  if (['mp3', 'ogg', 'wav'].includes(ext)) return <Music className="w-4 h-4 text-pink-400" />;
  if (['db', 'sqlite', 'sql'].includes(ext)) return <DatabaseIcon className="w-4 h-4 text-orange-400" />;
  if (['jar', 'zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) return <Archive className="w-4 h-4 text-amber-400" />;
  if (['txt', 'log', 'md'].includes(ext)) return <FileText className="w-4 h-4 text-text-muted" />;
  return <FileIcon className="w-4 h-4 text-text-muted" />;
}

function isEditable(file: ServerFile): boolean {
  if (!file.isFile || file.size > MAX_EDITABLE_SIZE) return false;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const editableExts = [
    'txt', 'log', 'md', 'json', 'yml', 'yaml', 'xml', 'properties',
    'cfg', 'conf', 'ini', 'toml', 'js', 'ts', 'py', 'java', 'sh',
    'bash', 'sk', 'html', 'css', 'sql', 'mcmeta', 'csv',
  ];
  return editableExts.includes(ext) || file.mimetype.startsWith('text/');
}

function isArchive(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return ['zip', 'tar', 'gz', 'tar.gz', 'tgz', '7z', 'rar'].includes(ext);
}

function joinPath(dir: string, name: string): string {
  if (dir === '/') return `/${name}`;
  return `${dir}/${name}`;
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  server: HostedServer;
  serverId: string;
}

const FilesTab: React.FC<Props> = ({ server, serverId }) => {
  // State
  const [currentDir, setCurrentDir] = useState('/');
  const [files, setFiles] = useState<ServerFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Editor state
  const [editingFile, setEditingFile] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingFile, setLoadingFile] = useState(false);

  // Modals
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [showNewFile, setShowNewFile] = useState(false);
  const [showRename, setShowRename] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newName, setNewName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Upload
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // ── Load files ──────────────────────────────────────────────────────────
  const loadFiles = useCallback(async (dir = currentDir) => {
    setLoading(true);
    try {
      const data = await hostingAPI.listFiles(serverId, dir);
      // Sort: folders first, then alphabetically
      const sorted = data.files.sort((a, b) => {
        if (a.isFile !== b.isFile) return a.isFile ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
      setFiles(sorted);
      setSelected(new Set());
    } catch (err) {
      console.error('[files] load error:', err);
    }
    setLoading(false);
  }, [serverId, currentDir]);

  useEffect(() => {
    loadFiles(currentDir);
  }, [currentDir, serverId]);

  // ── Navigation ──────────────────────────────────────────────────────────
  const navigateTo = (dir: string) => {
    setEditingFile(null);
    setCurrentDir(dir);
  };

  const navigateUp = () => {
    if (currentDir === '/') return;
    const parts = currentDir.split('/').filter(Boolean);
    parts.pop();
    navigateTo(parts.length ? `/${parts.join('/')}` : '/');
  };

  const breadcrumbs = currentDir === '/'
    ? ['/']
    : ['/', ...currentDir.split('/').filter(Boolean)];

  // ── File actions ────────────────────────────────────────────────────────
  const handleOpen = async (file: ServerFile) => {
    if (!file.isFile) {
      navigateTo(joinPath(currentDir, file.name));
      return;
    }
    if (isEditable(file)) {
      setLoadingFile(true);
      try {
        const data = await hostingAPI.getFileContent(serverId, joinPath(currentDir, file.name));
        setEditorContent(data.content);
        setOriginalContent(data.content);
        setEditingFile(file.name);
      } catch (err) {
        console.error('[files] read error:', err);
      }
      setLoadingFile(false);
    }
  };

  const handleSave = async () => {
    if (!editingFile) return;
    setSaving(true);
    try {
      await hostingAPI.writeFileContent(serverId, joinPath(currentDir, editingFile), editorContent);
      setOriginalContent(editorContent);
    } catch (err) {
      console.error('[files] save error:', err);
    }
    setSaving(false);
  };

  const handleCreateFolder = async () => {
    if (!newName.trim()) return;
    setActionLoading(true);
    try {
      await hostingAPI.createFolder(serverId, currentDir, newName.trim());
      setShowNewFolder(false);
      setNewName('');
      loadFiles();
    } catch (err) {
      console.error('[files] create folder error:', err);
    }
    setActionLoading(false);
  };

  const handleCreateFile = async () => {
    if (!newName.trim()) return;
    setActionLoading(true);
    try {
      await hostingAPI.writeFileContent(serverId, joinPath(currentDir, newName.trim()), '');
      setShowNewFile(false);
      setNewName('');
      loadFiles();
    } catch (err) {
      console.error('[files] create file error:', err);
    }
    setActionLoading(false);
  };

  const handleRename = async () => {
    if (!showRename || !newName.trim()) return;
    setActionLoading(true);
    try {
      await hostingAPI.renameFiles(serverId, currentDir, [{ from: showRename, to: newName.trim() }]);
      setShowRename(null);
      setNewName('');
      loadFiles();
    } catch (err) {
      console.error('[files] rename error:', err);
    }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    const items = Array.from(selected);
    if (!items.length) return;
    setActionLoading(true);
    try {
      await hostingAPI.deleteFiles(serverId, currentDir, items);
      setShowDeleteConfirm(false);
      setSelected(new Set());
      if (editingFile && items.includes(editingFile)) setEditingFile(null);
      loadFiles();
    } catch (err) {
      console.error('[files] delete error:', err);
    }
    setActionLoading(false);
  };

  const handleCompress = async () => {
    const items = Array.from(selected);
    if (!items.length) return;
    setActionLoading(true);
    try {
      await hostingAPI.compressFiles(serverId, currentDir, items);
      loadFiles();
    } catch (err) {
      console.error('[files] compress error:', err);
    }
    setActionLoading(false);
  };

  const handleDecompress = async (filename: string) => {
    setActionLoading(true);
    try {
      await hostingAPI.decompressFile(serverId, currentDir, filename);
      loadFiles();
    } catch (err) {
      console.error('[files] decompress error:', err);
    }
    setActionLoading(false);
  };

  const handleDownload = async (filename: string) => {
    try {
      const data = await hostingAPI.getFileDownloadUrl(serverId, joinPath(currentDir, filename));
      window.open(data.url, '_blank');
    } catch (err) {
      console.error('[files] download error:', err);
    }
  };

  const handleUpload = async (fileList: FileList) => {
    setUploading(true);
    try {
      const data = await hostingAPI.getFileUploadUrl(serverId, currentDir);
      const formData = new FormData();
      for (const file of Array.from(fileList)) {
        formData.append('files', file);
      }
      await fetch(data.url, { method: 'POST', body: formData });
      loadFiles();
    } catch (err) {
      console.error('[files] upload error:', err);
    }
    setUploading(false);
  };

  const toggleSelect = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === files.length) setSelected(new Set());
    else setSelected(new Set(files.map((f) => f.name)));
  };

  const hasChanges = editingFile && editorContent !== originalContent;

  // ── Drag & Drop ─────────────────────────────────────────────────────────
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length) {
      handleUpload(e.dataTransfer.files);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div
      className="h-full flex flex-col overflow-hidden relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-cyan-500/10 border-2 border-dashed border-cyan-500/50 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-cyan-400">
            <Upload className="w-10 h-10" />
            <span className="text-sm font-medium">Déposer les fichiers ici</span>
          </div>
        </div>
      )}

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 px-4 py-2 border-b border-border-color bg-bg-secondary/50 flex items-center gap-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-0.5 text-xs text-text-muted flex-1 min-w-0 overflow-x-auto">
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0 text-text-muted/50" />}
              <button
                onClick={() => {
                  if (i === 0) navigateTo('/');
                  else navigateTo('/' + breadcrumbs.slice(1, i + 1).join('/'));
                }}
                className="hover:text-text-primary transition-colors truncate flex-shrink-0 px-1 py-0.5 rounded hover:bg-bg-tertiary"
              >
                {crumb === '/' ? '~' : crumb}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected.size > 0 && (
            <>
              <span className="text-[10px] text-text-muted mr-1">{selected.size} sélectionné{selected.size > 1 ? 's' : ''}</span>
              <button
                onClick={handleCompress}
                disabled={actionLoading}
                className="p-1.5 rounded-md text-text-muted hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
                title="Compresser"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={actionLoading}
                className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-4 bg-border-color mx-0.5" />
            </>
          )}

          <button
            onClick={() => { setNewName(''); setShowNewFolder(true); }}
            className="p-1.5 rounded-md text-text-muted hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
            title="Nouveau dossier"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { setNewName(''); setShowNewFile(true); }}
            className="p-1.5 rounded-md text-text-muted hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
            title="Nouveau fichier"
          >
            <FilePlus className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => uploadInputRef.current?.click()}
            disabled={uploading}
            className="p-1.5 rounded-md text-text-muted hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
            title="Uploader"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          </button>
          <input
            ref={uploadInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
          <button
            onClick={() => loadFiles()}
            className="p-1.5 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Content area ────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden">
        {/* File list */}
        <div className={`${editingFile ? 'w-72 border-r border-border-color' : 'flex-1'} flex flex-col overflow-hidden transition-all`}>
          {/* Select all */}
          {files.length > 0 && (
            <div className="flex-shrink-0 px-4 py-1.5 border-b border-border-color/50 bg-bg-secondary/30">
              <label className="flex items-center gap-2 text-[10px] text-text-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.size === files.length && files.length > 0}
                  onChange={selectAll}
                  className="w-3 h-3 rounded border-border-color bg-bg-primary accent-cyan-500"
                />
                Tout sélectionner
              </label>
            </div>
          )}

          {/* File list */}
          <div className="flex-1 overflow-y-auto gaming-scrollbar">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : (
              <>
                {/* Back button */}
                {currentDir !== '/' && (
                  <button
                    onClick={navigateUp}
                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-bg-tertiary transition-colors text-left group"
                  >
                    <ArrowLeft className="w-4 h-4 text-text-muted group-hover:text-text-primary" />
                    <span className="text-xs text-text-muted group-hover:text-text-primary">..</span>
                  </button>
                )}

                {files.length === 0 && !loading && (
                  <div className="text-center py-12 text-text-muted text-xs">
                    <Folder className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Dossier vide
                  </div>
                )}

                {files.map((file) => (
                  <div
                    key={file.name}
                    onDoubleClick={() => handleOpen(file)}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey) toggleSelect(file.name, e);
                    }}
                    className={`flex items-center gap-3 px-4 py-1.5 hover:bg-bg-tertiary transition-colors cursor-default group ${
                      selected.has(file.name) ? 'bg-cyan-500/5' : ''
                    } ${editingFile === file.name ? 'bg-cyan-500/10 border-r-2 border-cyan-500' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(file.name)}
                      onChange={() => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(file.name)) next.delete(file.name);
                          else next.add(file.name);
                          return next;
                        });
                      }}
                      className="w-3 h-3 rounded border-border-color bg-bg-primary accent-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      style={selected.has(file.name) ? { opacity: 1 } : {}}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {getFileIcon(file)}
                    <span
                      className="flex-1 text-xs text-text-primary truncate cursor-pointer"
                      onClick={() => handleOpen(file)}
                    >
                      {file.name}
                    </span>
                    <span className="text-[10px] text-text-muted flex-shrink-0">
                      {file.isFile ? formatSize(file.size) : ''}
                    </span>

                    {/* Quick actions */}
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      {file.isFile && isArchive(file.name) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDecompress(file.name); }}
                          className="p-1 rounded text-text-muted hover:text-purple-400 hover:bg-purple-500/10"
                          title="Décompresser"
                        >
                          <ArchiveRestore className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setNewName(file.name); setShowRename(file.name); }}
                        className="p-1 rounded text-text-muted hover:text-cyan-400 hover:bg-cyan-500/10"
                        title="Renommer"
                      >
                        <Edit3 className="w-3 h-3" />
                      </button>
                      {file.isFile && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(file.name); }}
                          className="p-1 rounded text-text-muted hover:text-emerald-400 hover:bg-emerald-500/10"
                          title="Télécharger"
                        >
                          <Download className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(new Set([file.name]));
                          setShowDeleteConfirm(true);
                        }}
                        className="p-1 rounded text-text-muted hover:text-red-400 hover:bg-red-500/10"
                        title="Supprimer"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── Editor panel ────────────────────────────────────────────────── */}
        {editingFile && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Editor header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-border-color bg-bg-secondary/30">
              <div className="flex items-center gap-2 min-w-0">
                <FileCode className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                <span className="text-xs font-medium text-text-primary truncate">{editingFile}</span>
                {hasChanges && (
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" title="Non sauvegardé" />
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/25 disabled:opacity-30 transition-colors"
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Sauvegarder
                </button>
                <button
                  onClick={() => setEditingFile(null)}
                  className="p-1 rounded-md text-text-muted hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Monaco editor */}
            {loadingFile ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-hidden">
                <Editor
                  height="100%"
                  language={getFileLanguage(editingFile)}
                  value={editorContent}
                  onChange={(value) => setEditorContent(value || '')}
                  theme="vs-dark"
                  options={{
                    fontSize: 12,
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                    minimap: { enabled: false },
                    lineNumbers: 'on',
                    scrollBeyondLastLine: false,
                    wordWrap: 'on',
                    tabSize: 2,
                    renderWhitespace: 'selection',
                    smoothScrolling: true,
                    padding: { top: 8 },
                    bracketPairColorization: { enabled: true },
                  }}
                  onMount={(editor) => {
                    // Ctrl+S / Cmd+S to save
                    editor.addCommand(
                      // eslint-disable-next-line no-bitwise
                      2048 | 49, // KeyMod.CtrlCmd | KeyCode.KeyS
                      () => handleSave(),
                    );
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                            */}
      {/* ═══════════════════════════════════════════════════════════════════ */}

      {/* New folder modal */}
      {showNewFolder && (
        <InlineModal
          title="Nouveau dossier"
          icon={<FolderPlus className="w-4 h-4 text-cyan-400" />}
          value={newName}
          onChange={setNewName}
          placeholder="Nom du dossier"
          onConfirm={handleCreateFolder}
          onCancel={() => setShowNewFolder(false)}
          loading={actionLoading}
        />
      )}

      {/* New file modal */}
      {showNewFile && (
        <InlineModal
          title="Nouveau fichier"
          icon={<FilePlus className="w-4 h-4 text-cyan-400" />}
          value={newName}
          onChange={setNewName}
          placeholder="Nom du fichier (ex: config.yml)"
          onConfirm={handleCreateFile}
          onCancel={() => setShowNewFile(false)}
          loading={actionLoading}
        />
      )}

      {/* Rename modal */}
      {showRename && (
        <InlineModal
          title={`Renommer "${showRename}"`}
          icon={<Edit3 className="w-4 h-4 text-cyan-400" />}
          value={newName}
          onChange={setNewName}
          placeholder="Nouveau nom"
          onConfirm={handleRename}
          onCancel={() => setShowRename(null)}
          loading={actionLoading}
        />
      )}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-bg-secondary border border-border-color rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <h3 className="text-sm font-bold text-text-primary">Confirmer la suppression</h3>
            </div>
            <p className="text-xs text-text-muted mb-4">
              Supprimer {selected.size} élément{selected.size > 1 ? 's' : ''} ?
              <br />
              <span className="text-red-300/80">Cette action est irréversible.</span>
            </p>
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => { setShowDeleteConfirm(false); }}
                className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
              >
                {actionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Inline modal (reusable for new folder / new file / rename) ───────────────

const InlineModal: React.FC<{
  title: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
}> = ({ title, icon, value, onChange, placeholder, onConfirm, onCancel, loading }) => (
  <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="bg-bg-secondary border border-border-color rounded-xl p-5 max-w-sm w-full mx-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-bold text-text-primary">{title}</h3>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') onConfirm();
          if (e.key === 'Escape') onCancel();
        }}
        className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors mb-3"
      />
      <div className="flex items-center gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-text-muted hover:text-text-primary transition-colors">
          Annuler
        </button>
        <button
          onClick={onConfirm}
          disabled={loading || !value.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Confirmer
        </button>
      </div>
    </div>
  </div>
);

export default FilesTab;
